﻿/* global AbortController */
(() => {
  if (customElements.get('ayaq-contact')) return;
  const PRODUCT_NAVIGATION_RESTORE_KEY = 'ayaq-contact:restore-after-product-navigation';
  class AyaqContact extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.dialog = this.querySelector('dialog');
      this.launcher = this.querySelector('[data-launcher]');
      // Keep the Liquid-provided hidden state until the public configuration
      // has resolved. Revealing it here caused a visible flash on mobile when
      // the contact center was then disabled or still loading.
      this.launcherHint = this.querySelector('[data-launcher-hint]');
      this.launcherHint?.setAttribute('hidden', '');
      this.launcher.removeAttribute('aria-describedby');
      this.launcher.addEventListener('click', () => this.open(), options);
      document.addEventListener('ayaq:contact-search', () => this.openSearch(), options);
      this.querySelector('[data-close]').addEventListener(
        'click',
        () => this.dialog.close(),
        options,
      );
      this.querySelector('[data-back]').addEventListener(
        'click',
        () => this.showView('chat'),
        options,
      );
      // Always intercept before lazy imports, even if the backend is unconfigured.
      this.addEventListener(
        'submit',
        (event) => {
          if (
            !event.target.matches(
              '[data-chat-form], [data-email-form], [data-followup-form]',
            )
          ) {
            return;
          }
          event.preventDefault();
          const isAssistantTurn = () =>
            event.target.matches('[data-chat-form]') &&
            this.outdoorMode === 'assistant';
          const loadClient = async () => {
            await this.contactBarLoading;
            return isAssistantTurn()
              ? this.loadAssistantChat()
              : this.loadHumanChat();
          };
          const client = isAssistantTurn() ? this.assistantChat : this.humanChat;
          if (!client) {
            loadClient().then(() => {
              if (isAssistantTurn() ? this.assistantChat : this.humanChat) {
                this.submit(event.target);
              } else this.showError(this.dataset.error);
            });
          } else {
            this.chat = client;
            this.submit(event.target);
          }
        },
        options,
      );
      this.dialog.addEventListener(
        'close',
        () => {
          this.launcher.setAttribute('aria-expanded', 'false');
          if (!this.assistantOpening) {
            this.launcher.focus({ preventScroll: true });
          }
          clearInterval(this.statusTimer);
          this.contactSearch?.reset();
        },
        options,
      );
      this.dialog.addEventListener(
        'click',
        (event) => {
          const r = this.dialog.getBoundingClientRect();
          if (
            event.target === this.dialog &&
            (event.clientX < r.left ||
              event.clientX > r.right ||
              event.clientY < r.top ||
              event.clientY > r.bottom)
          ) {
            this.dialog.close();
          }
        },
        options,
      );
      this.querySelectorAll('[data-action]').forEach((button) =>
        button.addEventListener(
          'click',
          async () => {
            if (button.dataset.action === 'assistant') {
              this.openAssistant(button);
            } else if (button.dataset.action === 'search') {
              this.openSearch();
            } else if (button.dataset.action === 'chatbot') {
              button.disabled = true;
              try {
                this.outdoorMode = 'assistant';
                await this.loadAssistantChat();
                if (!this.chat) throw Error('unavailable');
                this.showView('chat');
              } catch {
                this.showError(this.dataset.assistantError);
              } finally {
                button.disabled = false;
              }
            } else if (button.dataset.action === 'chat') {
              button.disabled = true;
              try {
                this.outdoorMode = 'human';
                await this.loadHumanChat();
                if (!this.chat) throw Error('unavailable');
                await this.chat.activateHuman();
                this.showView('chat');
              } catch {
                this.showError(this.dataset.error);
              } finally {
                button.disabled = false;
              }
            } else this.showView(button.dataset.action);
          },
          { ...options, capture: true },
        ),
      );
      this.querySelector('[data-new-question]').addEventListener(
        'click',
        () => this.chat?.newQuestion(),
        options,
      );
      document.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest?.('[data-ayaq-product-question]');
          const question = button?.dataset.ayaqProductQuestion?.trim();
          if (!question) return;
          event.preventDefault();
          this.askSuggestedProductQuestion(question);
        },
        options,
      );
      const resize = () => {
        const viewport = window.visualViewport;
        if (viewport && this.dialog.open) {
          this.style.setProperty('--contact-height', `${viewport.height}px`);
          this.style.setProperty('--contact-top', `${viewport.offsetTop}px`);
          this.resizeComposer();
        }
      };
      this.resize = resize;
      window.visualViewport?.addEventListener('resize', resize, options);
      window.visualViewport?.addEventListener('scroll', resize, options);
      window.addEventListener('pagehide', () => this.humanChat?.pause(), options);
      window.addEventListener('pageshow', () => this.humanChat?.resume(), options);
      document.addEventListener(
        'visibilitychange',
        () => {
          if (!document.hidden && this.dialog.open) this.refreshPresence();
        },
        options,
      );
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          if (this.contains(event.target)) this.open();
        },
        options,
      );
      const body = this.querySelector('[data-chat-form] textarea');
      body.addEventListener('input', () => this.resizeComposer(), options);
      body.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
            event.preventDefault();
            if (body.value.trim()) body.form.requestSubmit();
          }
        },
        options,
      );
      this.updateAvailability();
      this.bindAssistant();
      this.resizeComposer();
      this.contactBarLoading = this.loadContactBar();
      this.contactBarLoading
        .then(() => this.restoreAssistantAfterProductNavigation())
        .catch(() => {});
    }
    prepareAssistantProductNavigation() {
      try {
        window.sessionStorage?.setItem(PRODUCT_NAVIGATION_RESTORE_KEY, '1');
      } catch {
        // Browser storage is optional; the product link must still work.
      }
    }
    consumeAssistantProductNavigationRestore() {
      try {
        const restore =
          window.sessionStorage?.getItem(PRODUCT_NAVIGATION_RESTORE_KEY) === '1';
        window.sessionStorage?.removeItem(PRODUCT_NAVIGATION_RESTORE_KEY);
        return restore;
      } catch {
        return false;
      }
    }
    async restoreAssistantAfterProductNavigation() {
      if (!this.consumeAssistantProductNavigationRestore()) return;
      try {
        this.outdoorMode = 'assistant';
        await this.loadAssistantChat();
        if (!this.assistantChat) return;
        await this.assistantChat.rehydration;
        this.chat = this.assistantChat;
        this.open();
      } catch {
        // The destination PDP remains fully usable when restoration fails.
      }
    }
    submit(form) {
      if (form.matches('[data-chat-form]')) this.chat.send();
      if (form.matches('[data-email-form]')) this.humanChat?.sendEmail();
      if (form.matches('[data-followup-form]')) this.humanChat?.saveIdentity();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.assistantChat?.destroy();
      if (this.humanChat !== this.assistantChat) this.humanChat?.destroy();
      this.contactSearch?.destroy();
      this.chat = null;
      this.assistantChat = null;
      this.humanChat = null;
      this.assistantChatLoading = null;
      this.humanChatLoading = null;
      clearInterval(this.statusTimer);
      clearTimeout(this.assistantTimeout);
      this.clearLauncherHint();
      this.dialog?.close();
      if (this.iadvize && this.assistantListener) {
        this.iadvize.off('chatbox:statusChange', this.assistantListener);
      }
    }
    open(view = 'chat') {
      this.rememberLauncherOpened();
      if (view !== 'search' && this.outdoorMode === 'assistant') {
        this.loadAssistantChat().catch(() => {});
      }
      if (!this.dialog.open) this.dialog.showModal();
      this.launcher.setAttribute('aria-expanded', 'true');
      this.resize();
      this.showView(view);
      this.refreshPresence();
      clearInterval(this.statusTimer);
      // Clock only: no HTTP polling.
      this.statusTimer = setInterval(() => this.updateAvailability(), 30000);
    }
    openSearch() {
      this.open('search');
    }
    async askSearchQuestion(question) {
      const prompt = question?.trim();
      if (!prompt) return;
      this.open('chat');
      try {
        this.outdoorMode = 'assistant';
        await this.loadAssistantChat();
        if (!this.chat) throw Error('assistant_unavailable');
        this.showView('chat', false);
        const body = this.querySelector('[data-chat-form] textarea');
        body.value = prompt;
        this.resizeComposer();
        this.chat.send();
      } catch {
        this.showError(this.dataset.assistantError);
      }
    }
    async askSuggestedProductQuestion(question) {
      this.open();
      try {
        this.outdoorMode = 'assistant';
        await this.loadAssistantChat();
        if (!this.chat) throw Error('assistant_unavailable');
        this.chat.newQuestion?.({ focus: false });
        const body = this.querySelector('[data-chat-form] textarea');
        body.value = question;
        this.resizeComposer();
        this.chat.send();
      } catch {
        this.showError(this.dataset.assistantError);
      }
    }
    shouldAutofocusComposer() {
      // On a phone, programmatic focus opens the virtual keyboard and hides
      // the reply the visitor just asked to read. Desktop keeps the efficient
      // keyboard flow once the panel reaches its desktop layout.
      const query = window.matchMedia?.('(width >= 750px)');
      return typeof query?.matches === 'boolean' ? query.matches : true;
    }
    showView(view, focus = true) {
      const email = view === 'email';
      const search = view === 'search';
      const conversation = this.querySelector('[data-conversation]');
      const emailView = this.querySelector('[data-email-view]');
      const searchView = this.querySelector('[data-search-view]');
      conversation && (conversation.hidden = email || search);
      emailView && (emailView.hidden = !email);
      searchView && (searchView.hidden = !search);
      this.querySelector('[data-back]').hidden = !email && !search;
      this.activeChannel = search
        ? 'search'
        : email
          ? 'email'
          : this.outdoorMode === 'human'
            ? 'chat'
            : 'chatbot';
      this.updateActiveChannel();
      this.showError('');
      this.chat?.markRead?.();
      this.resizeComposer();
      if (search) {
        this.loadSearch().then(() => this.contactSearch?.activate({ focus })).catch(() => {});
      } else if (focus && this.shouldAutofocusComposer()) {
        (email
          ? this.querySelector('[data-email-form] input')
          : this.querySelector('[data-chat-form] textarea')
        )?.focus({ preventScroll: true });
      }
    }
    updateActiveChannel() {
      const activeChannel = this.activeChannel || 'chatbot';
      this.querySelectorAll('.ayaq-contact__action[data-action]').forEach((button) =>
        button.setAttribute('aria-pressed', String(button.dataset.action === activeChannel)),
      );
    }
    resizeComposer() {
      const textarea = this.querySelector('[data-chat-form] textarea');
      const scroll = this.querySelector('[data-chat-scroll]');
      const atBottom =
        scroll.clientHeight > 0 &&
        scroll.scrollHeight - scroll.scrollTop - scroll.clientHeight < 100;
      textarea.style.height = 'auto';
      const maximum =
        Number.parseFloat(window.getComputedStyle(textarea).maxHeight) || 144;
      textarea.style.height = `${Math.min(maximum, textarea.scrollHeight)}px`;
      if (atBottom) scroll.scrollTop = scroll.scrollHeight;
      textarea.form.querySelector('[type="submit"]').disabled =
        !textarea.value.trim() || this.chat?.closed === true || this.chat?.sending === true;
    }
    scheduledOpen(now = new Date()) {
      if (this.dataset.availability !== 'auto') {
        return this.dataset.availability === 'open';
      }
      try {
        const parts = Object.fromEntries(
          new Intl.DateTimeFormat('en-GB', {
            timeZone: this.dataset.timezone,
            weekday: 'short',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
          })
            .formatToParts(now)
            .map((p) => [p.type, p.value]),
        );
        const day = this.querySelector(
          `[data-day="${parts.weekday.toLowerCase()}"]`,
        );
        if (!day || day.dataset.active !== 'true') return false;
        const minutes = Number(parts.hour) * 60 + Number(parts.minute);
        const parse = (v) =>
          /^([01]\d|2[0-3]):[0-5]\d$/.test(v || '')
            ? Number(v.slice(0, 2)) * 60 + Number(v.slice(3))
            : NaN;
        return [1, 2].some(
          (n) =>
            minutes >= parse(day.dataset[`start${n}`]) &&
            minutes < parse(day.dataset[`end${n}`]),
        );
      } catch {
        return false;
      }
    }
    async refreshPresence() {
      if (this.chat?.connected) {
        this.updateAvailability();
        return;
      }
      if (this.dataset.backend && !this.checkingPresence) {
        this.checkingPresence = true;
        try {
          const url = new window.URL('/api/presence', this.dataset.backend);
          if (url.protocol !== 'https:') throw Error('HTTPS required');
          const response = await fetch(url, {
            signal: window.AbortSignal.timeout(5000),
            credentials: 'omit',
            cache: 'no-store',
          });
          if (!response.ok) throw Error('unavailable');
          const presence = await response.json();
          this.applyPresence(presence);
          this.presenceExpires = Date.now() + 70000;
        } catch {
          // Presence only informs the human-support availability indicator. It
          // must never be presented as a failed visitor message, especially
          // when the Assistant channel remains available independently.
          this.operatorAvailable = false;
        } finally {
          this.checkingPresence = false;
        }
      }
      if (this.isConnected) this.updateAvailability();
    }
    updateAvailability() {
      this.available =
        !!this.dataset.backend &&
        this.dataset.availability !== 'closed' &&
        (this.serviceManaged || this.scheduledOpen()) &&
        this.operatorAvailable === true &&
        (!this.presenceExpires || this.presenceExpires > Date.now());
      const identity = this.querySelector('[data-identity]');
      // Identity never gates the first message, including when the service is closed.
      identity.hidden = true;
      identity.disabled = true;
      identity
        .querySelectorAll('input')
        .forEach((input) => (input.required = false));
      this.querySelectorAll('.ayaq-contact__presence').forEach(
        (dot) => (dot.hidden = !this.available),
      );
      const notice = this.chat?.closed
        ? this.dataset.closed
        : this.chat?.session?.id && !this.chat.connected
          ? this.dataset.connecting
          : this.outdoorMode === 'assistant'
            ? this.dataset.outdoorNotice
            : this.available
              ? this.dataset.online
              : this.serviceOfflineText || this.dataset.offline;
      const noticeElement = this.querySelector('[data-chat-notice]');
      noticeElement.textContent = notice || '';
      noticeElement.hidden = !notice;
      this.updateActiveChannel();
    }
    applyPresence(presence) {
      this.operatorAvailable = presence.available === true;
      if (presence.serviceManaged !== undefined) {
        this.serviceManaged = presence.serviceManaged === true;
      }
      const french = (document.documentElement.lang || 'fr').startsWith('fr');
      const text = french ? presence.offlineText : presence.offlineTextEn;
      if (typeof text === 'string') this.serviceOfflineText = text;
    }
    async loadContactBar() {
      try {
        const backend = new window.URL(this.dataset.backend);
        const configUrl = new window.URL(
          this.dataset.configUrl || '/api/contact-bar',
          backend,
        );
        if (configUrl.protocol !== 'https:') throw Error('HTTPS required');
        const response = await fetch(configUrl, {
          signal: window.AbortSignal.timeout(5000),
          credentials: 'omit',
          cache: 'no-store',
        });
        const projection = await response.json().catch(() => null);
        const channels = projection?.channels;
        if (
          !response.ok ||
          typeof projection?.enabled !== 'boolean' ||
          !channels ||
          !['chatbot', 'human', 'email', 'whatsapp', 'faq'].every(
            (channel) => typeof channels[channel] === 'boolean',
          )
        ) {
          throw Error('invalid_contact_bar_projection');
        }
        let assistant = null;
        if (channels.chatbot) {
          const endpoint = projection?.assistant?.endpoint;
          const url =
            typeof endpoint === 'string' ? new window.URL(endpoint) : null;
          if (
            projection?.assistant?.version !== 'ContactCenterAssistantV1' ||
            projection?.assistant?.method !== 'POST' ||
            !url ||
            url.protocol !== 'https:'
          ) {
            throw Error('invalid_contact_bar_assistant');
          }
          assistant = Object.freeze({ endpoint: url.href });
        }
        this.contactBar = Object.freeze({
          enabled: projection.enabled,
          channels: Object.freeze({ ...channels }),
          assistant,
        });
        // The visible composer belongs to Assistant AYAQ when that channel is
        // enabled. Without this default, the first message incorrectly enters
        // the legacy human-support path and fails while that service is offline.
        if (
          projection.enabled &&
          channels.chatbot &&
          assistant &&
          !this.outdoorMode
        ) {
          this.outdoorMode = 'assistant';
          this.updateAvailability();
        }
        this.querySelectorAll('[data-contact-channel]').forEach((element) => {
          const visible =
            projection.enabled &&
            (element.dataset.contactChannel === 'search' ||
              channels[element.dataset.contactChannel] === true);
          const item = element.closest('.ayaq-contact__item') || element;
          item.hidden = !visible;
        });
        this.updateLauncher();
      } catch {
        // Fail closed: without the public projection, a disabled channel cannot flash.
        this.contactBar = null;
        this.updateLauncher();
      }
    }
    updateLauncher() {
      const channels = this.contactBar?.channels;
      const hasChannel =
        this.contactBar?.enabled &&
        channels &&
        Object.values(channels).some(Boolean);
      this.launcher.hidden = !hasChannel || this.assistantOpen === true;
      if (this.launcher.hidden) this.clearLauncherHint();
      else this.queueLauncherHint();
    }
    canShowLauncherHint() {
      if (!this.launcherHint || this.launcher.hidden) return false;
      if (!window.matchMedia?.('(min-width: 750px)').matches) return false;
      try {
        return (
          window.localStorage?.getItem('ayaq-contact-opened') !== '1' &&
          window.sessionStorage?.getItem('ayaq-contact-hint-shown') !== '1'
        );
      } catch {
        return false;
      }
    }
    queueLauncherHint() {
      if (this.launcherHintTimer || !this.canShowLauncherHint()) return;
      this.launcherHintTimer = setTimeout(() => {
        this.launcherHintTimer = null;
        if (!this.canShowLauncherHint()) return;
        this.launcherHint.hidden = false;
        this.launcher.setAttribute('aria-describedby', this.launcherHint.id);
        try {
          window.sessionStorage?.setItem('ayaq-contact-hint-shown', '1');
        } catch {
          // Storage can be unavailable in private browsing; the hint stays optional.
        }
        this.launcherHintHideTimer = setTimeout(
          () => this.clearLauncherHint(),
          5000,
        );
      }, 1800);
    }
    clearLauncherHint() {
      clearTimeout(this.launcherHintTimer);
      clearTimeout(this.launcherHintHideTimer);
      this.launcherHintTimer = null;
      this.launcherHintHideTimer = null;
      this.launcherHint?.setAttribute('hidden', '');
      this.launcher?.removeAttribute?.('aria-describedby');
    }
    rememberLauncherOpened() {
      this.clearLauncherHint();
      try {
        window.localStorage?.setItem('ayaq-contact-opened', '1');
      } catch {
        // Opening the contact center must work even when persistent storage is denied.
      }
    }
    async loadSearch() {
      if (this.contactSearch) return this.contactSearch;
      if (this.contactSearchLoading) return this.contactSearchLoading;
      if (!this.dataset.searchModule) return null;
      const lifecycle = this.abort;
      this.contactSearchLoading = (async () => {
        try {
          const { ContactSearch } = await import(this.dataset.searchModule);
          if (
            this.isConnected &&
            lifecycle === this.abort &&
            !lifecycle.signal.aborted &&
            !this.contactSearch
          ) {
            this.contactSearch = new ContactSearch(this);
          }
        } finally {
          this.contactSearchLoading = null;
        }
        return this.contactSearch;
      })();
      return this.contactSearchLoading;
    }
    async loadAssistantChat() {
      await this.contactBarLoading;
      const contract = this.contactBar?.assistant;
      if (!contract || !this.dataset.assistantModule) return;
      if (this.assistantChat) {
        this.chat = this.assistantChat;
        return;
      }
      if (this.assistantChatLoading) return this.assistantChatLoading;
      const lifecycle = this.abort;
      this.assistantChatLoading = (async () => {
        try {
          const { ContactAssistant } = await import(this.dataset.assistantModule);
          if (
            this.isConnected &&
            lifecycle === this.abort &&
            !lifecycle.signal.aborted &&
            !this.assistantChat
          ) {
            this.assistantChat = new ContactAssistant(this, contract);
            this.chat = this.assistantChat;
          }
        } catch (error) {
          this.reportAssistantLoadFailure(error);
          this.showError(this.dataset.assistantError);
        } finally {
          this.assistantChatLoading = null;
          this.resizeComposer();
        }
      })();
      return this.assistantChatLoading;
    }
    reportAssistantLoadFailure(error) {
      // Keep the original error object (including its cause chain) available
      // in the browser console without exposing it to the visitor or sending
      // any client data to another service.
      console.error('AYAQ contact assistant load failed', {
        operation: 'contact.assistant.load',
        module: this.dataset.assistantModule || '',
        channel: 'assistant',
        error,
      });
    }
    async loadHumanChat() {
      if (!this.dataset.backend) return;
      if (this.humanChat) {
        this.chat = this.humanChat;
        return;
      }
      if (this.humanChatLoading) return this.humanChatLoading;
      const lifecycle = this.abort;
      this.humanChatLoading = (async () => {
        try {
          const { ContactChat } = await import(this.dataset.chatModule);
          if (
            this.isConnected &&
            lifecycle === this.abort &&
            !lifecycle.signal.aborted &&
            !this.humanChat
          ) {
            this.humanChat = new ContactChat(this);
            this.humanChat.setMode('human');
            this.chat = this.humanChat;
          }
        } catch {
          this.showError(this.dataset.error);
        } finally {
          this.humanChatLoading = null;
          this.resizeComposer();
        }
      })();
      return this.humanChatLoading;
    }
    showError(message) {
      const error = this.querySelector('[data-chat-error]');
      if (error) {
        error.textContent = message;
        error.hidden = !message;
      }
      // The contact fallback is optional in the current theme markup. A
      // missing optional panel must never prevent the chat client from loading.
      const fallback = this.querySelector('[data-chat-fallback]');
      if (fallback) fallback.hidden = !message;
    }
    bindAssistant() {
      window.iAdvizeInterface = window.iAdvizeInterface || [];
      const lifecycle = this.abort;
      window.iAdvizeInterface.push((api) => {
        if (!this.isConnected || lifecycle !== this.abort) return;
        this.iadvize = api;
        this.assistantListener = (state) => {
          this.assistantOpen = state === 'OPENED';
          if (this.assistantOpen) {
            clearTimeout(this.assistantTimeout);
            this.assistantOpening = false;
            if (this.dialog.open) this.dialog.close();
          }
          this.updateLauncher();
        };
        api.on('chatbox:statusChange', this.assistantListener);
        this.assistantListener(api.get('chatbox:status'));
      });
    }
    openAssistant(button) {
      const status = this.querySelector('[data-assistant-status]');
      if (button?.dataset.assistantState !== 'online') {
        status.hidden = false;
        status.textContent = this.dataset.assistantError;
        return;
      }
      this.assistantOpening = true;
      this.dialog.close();
      status.hidden = true;
      const failed = () => {
        this.assistantOpening = false;
        this.assistantOpen = false;
        this.updateLauncher();
        this.open();
        status.hidden = false;
        status.textContent = this.dataset.assistantError;
      };
      clearTimeout(this.assistantTimeout);
      this.assistantTimeout = setTimeout(failed, 7000);
      // The official selector binding receives this same genuine click. Capturing
      // closes our dialog first; never synthesize clicks or manipulate vendor DOM.
      if (this.iadvize?.get('chatbox:status') === 'OPENED') {
        this.assistantListener('OPENED');
      }
    }
  }
  customElements.define('ayaq-contact', AyaqContact);
})();
